import { useState, useCallback, useEffect, useMemo } from "react";
import { useAuth } from "../../context/AuthProvider";
import { BASE_URL } from "../../apiConfig"; 
import { toast } from 'sonner'; 
import {Link} from "react-router-dom";
import ChangePassword from "../../cors/ChangePassword";
import { Package, DollarSign, TrendingUp, Activity, Calendar } from 'lucide-react';
import { MdOutlineDeliveryDining as Moto } from "react-icons/md";
import { statuses } from "../../cors/statusMap";


export default function UpdateLivreurForm({ livreur }) {
  const { token, role, roles } = useAuth();
  const [formData, setFormData] = useState(livreur || {
    id: null,
    name: "",
    email: "",
    phone: "",
    prix_delivery: "",
    rib: "",
  });

  const getStatusColor = (status) => statuses[status]?.color || "bg-gray-100 text-gray-800 border-gray-200";
  const getStatusText = (status) => statuses[status]?.text || status;
  const getStatusIcon = (status, iconSize = 14) => {
    const Icon = statuses[status]?.icon;
    return Icon ? <Icon size={iconSize} className="mr-1" /> : null;
  };

  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({}); 
  const [apiError, setApiError] = useState("");

  const statistics = livreur?.statistics;
  const recentActivity = livreur?.recent_activity;

  useEffect(() => {
    if (livreur) {
      setFormData({
        ...livreur,
        email: livreur.user?.email || "",
      });
      setValidationErrors({}); 
      setApiError("");
    }
  }, [livreur]); 

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: undefined })); 
    }
    setApiError(""); 
  }, [validationErrors]); 

  // Form Submission Handler
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setValidationErrors({}); 
    setApiError(""); 

    if (!token) {
      toast.error("Authentification requise. Veuillez vous connecter."); 
      return;
    }

    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Le nom du livreur est requis.";
    }
    if (!formData.email.trim()) {
      newErrors.email = "L'email est requis.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "L'email n'est pas valide.";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Le numéro de téléphone est requis.";
    } else if (!/^\+?[0-9\s-()]{7,20}$/.test(formData.phone)) {
      newErrors.phone = "Veuillez entrer un numéro de téléphone valide.";
    }
    if (!formData.prix_delivery) {
        newErrors.prix_delivery = "Le prix de livraison est requis.";
    } else if (isNaN(formData.prix_delivery)) {
        newErrors.prix_delivery = "Le prix de livraison doit être un nombre.";
    }

    if (Object.keys(newErrors).length > 0) {
      setValidationErrors(newErrors);
      toast.error("Veuillez corriger les erreurs dans le formulaire.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${BASE_URL}/api/livreurs/${formData.id}`, {
        method: "PUT", 
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok) {
        if (response.status === 422 && result.errors) {
          setValidationErrors(result.errors);
          toast.error(result.message || "Veuillez corriger les champs mis en évidence."); 
        } else {
          const errorMessage = result.message || `Erreur: ${response.statusText}`; 
          setApiError(errorMessage);
          toast.error(errorMessage);
        }
        return; 
      }

      // Success:
      toast.success("Livreur mis à jour avec succès !"); 
      setFormData(result.data); 
      setValidationErrors({}); 
      setApiError("");

    } catch (error) {
      console.error("Erreur réseau ou inattendue lors de la mise à jour du livreur:", error); 
      setApiError("Une erreur réseau inattendue est survenue. Veuillez vérifier votre connexion."); 
      toast.error("Une erreur inattendue est survenue. Veuillez réessayer."); 
    } finally {
      setIsLoading(false);
    }
  }, [formData, token]); 

  return (
    <div className="max-w-5xl mx-auto min-h-screen px-4">
      <div className="mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mb-8 overflow-hidden">
        {/* Header Section */}
          <div className="bg-gradient-to-r from-[var(--color-primary-600)] via-[var(--color-primary-500)] to-[var(--color-primary-400)] px-6 py-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 md:w-32 md:h-32 bg-white/20 rounded-full border-4 border-white shadow-lg flex items-center justify-center">
                  <Moto className="h-8 w-8 md:w-16 md:h-16 text-white" />
                </div>
                <div className="text-white">
                  <h1 className="text-xl md:text-2xl font-bold">{formData.name}</h1>
                  <p className="text-emerald-100 text-xs">Gestion des livreurs</p>
                </div>
              </div>
              <div className="mt-4 md:mt-0">
                  <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg px-4 py-2 text-center text-white">
                    <div className="text-xl font-bold"> Livreur</div>
                    <div className="text-xs opacity-80">Score de confiance

</div>
                  </div>
              </div>
            </div>
          </div>

          {/* Statistics Section */}
          {statistics && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6">
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                      <div>
                          <p className="text-xs text-gray-500 font-medium mb-1">Total Livré</p>
                          <p className="text-xl font-bold text-gray-800">{statistics.total_delivered_orders}</p>
                      </div>
                      <div className="p-3 bg-blue-50 text-blue-500 rounded-lg">
                          <Package size={20} />
                      </div>
                  </div>
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                      <div>
                          <p className="text-xs text-gray-500 font-medium mb-1">Prix Unitaire</p>
                          <p className="text-xl font-bold text-gray-800">{statistics.unit_delivery_price} DH</p>
                      </div>
                      <div className="p-3 bg-purple-50 text-purple-500 rounded-lg">
                          <DollarSign size={20} />
                      </div>
                  </div>
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                      <div>
                          <p className="text-xs text-gray-500 font-medium mb-1">Gains Totaux Estimés</p>
                          <p className="text-xl font-bold text-emerald-600">{statistics.total_earnings} DH</p>
                      </div>
                      <div className="p-3 bg-emerald-50 text-emerald-500 rounded-lg">
                          <TrendingUp size={20} />
                      </div>
                  </div>
              </div>
          )}
        
          {/* API Error Display */}
          {apiError && (
            <div className="mb-6 mx-6 mt-6 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-center font-medium" role="alert">
              <p>{apiError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="p-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                 {/* Left Column */}
                <div className="space-y-6">
                     <div className="flex items-center mb-4">
                        <div className="bg-[var(--color-primary-50)] p-3 rounded-lg mr-4">
                            <svg className="w-6 h-6 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                        </div>
                        <h2 className="text-lg font-bold text-gray-800">Informations personnelles</h2>
                     </div>
                    {/* Livreur Name */}
                    <div>
                    <label htmlFor="name" className="block font-semibold text-gray-900 mb-2 text-sm">Nom du Livreur <span className="text-red-500">*</span></label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name || ''} 
                        onChange={handleInputChange}
                        placeholder="Entrez le nom du livreur..."
                        className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                        validationErrors.name
                            ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                            : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                        }`}
                        aria-invalid={!!validationErrors.name} 
                        aria-describedby={validationErrors.name ? "name-error" : undefined}
                    />
                    {validationErrors.name && <p id="name-error" className="mt-2 text-red-600 text-xs font-medium">{validationErrors.name[0]}</p>}
                    </div>

                    {/* Email */}
                    <div>
                    <label htmlFor="email" className="block font-semibold text-gray-900 mb-2 text-sm">Email <span className="text-red-500">*</span></label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email || ''} 
                        onChange={handleInputChange}
                        placeholder="Entrez l'email du livreur..."
                        className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                        validationErrors.email
                            ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                            : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                        }`}
                        aria-invalid={!!validationErrors.email} 
                        aria-describedby={validationErrors.email ? "email-error" : undefined}
                    />
                    {validationErrors.email && <p id="email-error" className="mt-2 text-red-600 text-xs font-medium">{validationErrors.email[0]}</p>}
                    </div>

                    {/* Phone */}
                    <div>
                    <label htmlFor="phone" className="block font-semibold text-gray-900 mb-2 text-sm">Téléphone <span className="text-red-500">*</span></label>
                    <input
                        type="tel" 
                        id="phone"
                        name="phone"
                        value={formData.phone || ''}
                        onChange={handleInputChange}
                        placeholder="Entrez le numéro de téléphone..."
                        className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                        validationErrors.phone
                            ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                            : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                        }`}
                        aria-invalid={!!validationErrors.phone}
                        aria-describedby={validationErrors.phone ? "phone-error" : undefined}
                    />
                    {validationErrors.phone && <p id="phone-error" className="mt-2 text-red-600 text-xs font-medium">{validationErrors.phone[0]}</p>}
                    </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                    <div className="flex items-center mb-4">
                        <div className="bg-[var(--color-primary-50)] p-3 rounded-lg mr-4">
                            <svg className="w-6 h-6 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h2 className="text-lg font-bold text-gray-800">Détails de Livraison & paiement</h2>
                     </div>

                    {/* Prix Delivery */}
                    <div>
                        <label htmlFor="prix_delivery" className="block text-sm font-semibold text-gray-900 mb-2">Prix de livraison (DH) *</label>
                        <input
                        type="number"
                        id="prix_delivery"
                        name="prix_delivery"
                        value={formData.prix_delivery || ''}
                        onChange={handleInputChange}
                        placeholder="Entrez le prix de livraison..."
                        className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                            validationErrors.prix_delivery
                            ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                            : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                        }`}
                        aria-invalid={!!validationErrors.prix_delivery}
                        aria-describedby={validationErrors.prix_delivery ? "prix-error" : undefined}
                        />
                        {validationErrors.prix_delivery && <p id="prix-error" className="mt-2 text-red-600 text-xs font-medium">{validationErrors.prix_delivery[0]}</p>}
                    </div>

                    {/* RIB */}
                    <div>
                        <label htmlFor="rib" className="block text-sm font-semibold text-gray-900 mb-2">RIB (Optionnel)</label>
                        <input
                        type="text"
                        id="rib"
                        name="rib"
                        value={formData.rib || ''}
                        onChange={handleInputChange}
                        placeholder="Entrez le RIB..."
                        className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                            validationErrors.rib
                            ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                            : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                        }`}
                        aria-invalid={!!validationErrors.rib}
                        aria-describedby={validationErrors.rib ? "rib-error" : undefined}
                        />
                        {validationErrors.rib && <p id="rib-error" className="mt-2 text-red-600 text-xs font-medium">{validationErrors.rib[0]}</p>}
                    </div>
                </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200 mt-10">
              <Link to="/livreurs" className="block text-center flex-1 py-3 px-4 cursor-pointer text-sm border-2 border-gray-200 text-gray-700 font-semibold rounded-2xl hover:border-gray-300 hover:bg-gray-50 transition-all duration-200">
                <button
                  type="button"
                  disabled={isLoading}
                  className="cursor-pointer"
                >
                  Annuler  
                </button>
              </Link> 
              <button
                type="submit"
                disabled={isLoading}
                className={`flex-1 py-3 cursor-pointer px-4 text-sm font-semibold rounded-2xl flex items-center justify-center gap-2 transition-all duration-200 ${
                  isLoading
                    ? "bg-gray-400 text-white cursor-not-allowed"
                    : "bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] text-white shadow-lg hover:shadow-xl transform hover:scale-[1.01]" 
                }`}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Enregistrement...
                  </>
                ) : (
                  <>
                    Enregistrer
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /> {/* Checkmark icon */}
                    </svg>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Recent Activity Section */}
        {recentActivity && recentActivity.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mb-8 overflow-hidden p-6">
                 <div className="flex items-center mb-6">
                    <div className="bg-blue-50 p-2 rounded-lg mr-3">
                        <Activity className="text-blue-500 w-5 h-5" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">Activité Récente</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="text-xs text-gray-500 border-b border-gray-100">
                                <th className="py-3 font-medium">Commande</th>
                                <th className="py-3 font-medium">Date de l'action</th>
                                <th className="py-3 text-center font-medium">Statut</th>
                                <th className="py-3 text-center font-medium">Total</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm">
                            {recentActivity.map((order) => (
                                <tr key={order.id} className="border-b border-gray-50 hover:bg-gray-50">
                                    <td className="py-3 font-medium text-xs text-gray-800">#{order.code}</td>
                                    <td className="py-3 text-gray-500 text-xs flex items-center gap-1">
                                        <Calendar size={12} />
                                        {new Date(order.action_date).toLocaleDateString('en-GB').replace(/^(\d{2})\/(\d{2})\/(\d{4})$/, '$1/$2/$3')}
                                    </td>
                                    <td className="py-3 ">
                                        <span className={`px-2 py-0.5 mx-auto flex items-center max-w-max text-nowrap rounded-full text-[11px] font-medium ${getStatusColor(order.action_status)}`}>
                                            {getStatusIcon(order.action_status)}
                                            {getStatusText(order.action_status)}
                                        </span>
                                    </td>
                                    <td className="py-3 text-center text-nowrap text-green-600 font-bold">{order.total} MAD</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        )}
        
        {/* Change Password Section */}
        {
        (role === 1 || (role === 2 && roles.edit_fournisseur === 1)) && // Using similar permission logic
          <ChangePassword userId={livreur?.livreur_id} />  
        }
      </div>
    </div>
  );
}
